import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonIcon from '@mui/icons-material/Person';
import InputAdornment from '@mui/material/InputAdornment';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
} from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDTypography from 'components/MDTypography';
import { useMaterialUIController } from 'context';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import {
  allowOnlyAlphanumericAndSpace,
  isValidEmail,
  isValidPhone,
} from "../../utils/Constant";

// Default severity levels - will be updated with dynamic data from API
const DEFAULT_severityLevels = [
  {
    value: 'critical',
    label: 'Critical',
    color: '#DC143C',
    description: 'Immediate safety concern or complete failure',
    includes: ['Ground failures', 'Overcurrent', 'High temperature', 'Power meter failures', 'Emergency stop', 'EV communication errors'],
  },
  {
    value: 'high',
    label: 'High',
    color: '#FF4500',
    description: 'Charger cannot operate, needs immediate attention',
    includes: ['Connector lock failures', 'Power switch failures', 'Reader failures', 'Under/Over voltage', 'Internal errors'],
  },
  {
    value: 'medium',
    label: 'Medium',
    color: '#FF8C00',
    description: 'Charger may operate with limitations',
    includes: ['Weak signal', 'Local list conflicts', 'Suspended EVSE', 'Other errors'],
  },
  {
    value: 'low',
    label: 'Low',
    color: '#FFA500',
    description: 'Minor issues, monitoring required',
    includes: ['No error', 'Reserved status', 'Available status'],
  },
];

const NOTIFICATION_TYPES = [
  {
    value: 'charger_fault',
    label: 'Charger Faults',
    icon: '⚡',
    description: 'Connector faults, OCPP errors',
  },
  {
    value: 'charger_offline',
    label: 'Charger Offline',
    icon: '📡',
    description: 'Charger offline/no heartbeat',
  },
  {
    value: 'charger_maintenance',
    label: 'Maintenance',
    icon: '🔧',
    description: 'Scheduled maintenance alerts',
  },
  {
    value: 'system_alert',
    label: 'System Alerts',
    icon: '🔔',
    description: 'System-level notifications',
  },
  {
    value: 'info',
    label: 'Information',
    icon: 'ℹ️',
    description: 'Informational messages',
  },
];

// Live Preview Component - Now uses dynamic severity includes
const NotificationPreview = ({ selectedTypes, selectedSeverities, darkMode, severityLevels }) => {
  // Don't show preview if nothing is selected
  if (selectedTypes.length === 0 || selectedSeverities.length === 0) {
    return null;
  }

  const combinations = [];

  selectedTypes.forEach(type => {
    selectedSeverities.forEach(severity => {
      const typeInfo = NOTIFICATION_TYPES.find(t => t.value === type);
      const severityInfo = severityLevels.find(s => s.value === severity);
      // Get dynamic includes from severity level
      const severityIncludes = severityInfo?.includes || [];
      combinations.push({
        type: typeInfo?.label || type,
        severity: severityInfo?.label || severity,
        typeIcon: typeInfo?.icon || '',
        severityColor: severityInfo?.color || '#808080',
        includes: Array.isArray(severityIncludes) ? severityIncludes.join(', ') : severityIncludes,
      });
    });
  });

  return (
    <MDBox
      mt={2}
      p={2.5}
      borderRadius="8px"
      sx={{
        backgroundColor: darkMode ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.1)',
        border: `1px solid ${darkMode ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'}`,
      }}
    >
      <MDBox display="flex" alignItems="center" gap={1} mb={2}>
        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 20 }} />
        <MDTypography variant="button" fontWeight="bold" color={darkMode ? 'white' : 'dark'}>
          You Will Receive Notifications For:
        </MDTypography>
      </MDBox>

      <MDBox>
        {combinations.map(({ type, severity, typeIcon, severityColor, includes }, index) => (
          <MDBox key={index} mb={2} p={1.5} borderRadius="6px" sx={{ backgroundColor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
            <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
              <MDTypography variant="body2" fontWeight="medium" color={darkMode ? 'white' : 'dark'}>
                {typeIcon} {type} + <span style={{ color: severityColor }}>{severity}</span> Severity
              </MDTypography>
            </MDBox>
            <MDTypography variant="caption" color="text" display="block" sx={{ pl: 3, fontStyle: 'italic' }}>
              <strong>Includes:</strong> {includes}
            </MDTypography>
          </MDBox>
        ))}

        <Divider sx={{ my: 1.5 }} />

        <MDBox display="flex" alignItems="flex-start" gap={1}>
          <MDTypography variant="caption" color="text" fontWeight="bold">
            ⚠️ Note:
          </MDTypography>
          <MDTypography variant="caption" color="text" display="block">
            You will NOT receive notifications for types or severities not selected above.
          </MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
};

function NotificationRecipientSettings({ recipients: existingRecipients = [], onRefresh, onSave }) {
  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;
  const { enqueueSnackbar } = useSnackbar();

  // Dynamic severity levels from API
  const [severityLevels, setSeverityLevels] = useState(DEFAULT_severityLevels);

  const [newRecipient, setNewRecipient] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    severity: [], // Default: nothing selected
    notificationType: [], // Default: nothing selected
    locationTags: [],
  });
  const [loadingRecipients, setLoadingRecipients] = useState({});

  // Location-related state
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');

  // Fetch dynamic severity info from API on mount
  useEffect(() => {
    const fetchSeverityInfo = async () => {
      try {
        const apiRequest = (await import('utils/Apirequest')).default;
        const request = apiRequest();
        const { data } = await request('get', 'notifications/severity-info', null, {}, {}, false, false);

        if (data?.success && data?.data) {
          // Transform API response to match component format
          const apiSeverityData = data.data;
          const transformedLevels = Object.entries(apiSeverityData).map(([key, value]) => ({
            value: key,
            label: value.label,
            color: value.color,
            description: value.description,
            includes: value.includes || [],
          }));

          // Sort by severity order: critical, high, medium, low
          const severityOrder = ['critical', 'high', 'medium', 'low'];
          transformedLevels.sort((a, b) =>
            severityOrder.indexOf(a.value) - severityOrder.indexOf(b.value)
          );

          setSeverityLevels(transformedLevels);
        }
      } catch (error) {
        console.error('Error fetching severity info:', error);
        // Keep default severity levels on error
      }
    };

    fetchSeverityInfo();
  }, []);

  // Fetch locations when component mounts
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const apiRequest = (await import('utils/Apirequest')).default;
        const request = apiRequest();
        const { data } = await request('get', 'charger-locations', null, {}, {});
        if (data?.success) {
          setLocations(data.data || []);
        }
      } catch (error) {
        console.error('Error fetching locations:', error);
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [recipientToDelete, setRecipientToDelete] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [recipientToEdit, setRecipientToEdit] = useState(null);
  const [editRecipient, setEditRecipient] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    severity: [],
    notificationType: [],
    locationTags: [],
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    notificationType: "",
    severity: "",
  });

  const validateName = (value) => {
    if (!value) return "Name is required";
    if (!allowOnlyAlphanumericAndSpace(value))
      return "Only alphabets and spaces allowed";
    if (!/^[A-Za-z ]+$/.test(value))
      return "Only alphabets and spaces allowed";
    return "";
  };

  const validateEmail = (value) => {
    if (!value) return "Email is required";
    if (!isValidEmail(value)) return "Enter a valid email address";
    return "";
  };

  const validatePhone = (value) => {
    if (!value) return "";
    if (!isValidPhone(value)) return "Enter a valid phone number";
    return "";
  };

  const validateNotificationTypes = () => {
    if (newRecipient.notificationType.length === 0) {
      return "Select at least one notification type";
    }
    return "";
  };

  const validateSeverity = () => {
    if (newRecipient.severity.length === 0) {
      return "Select at least one severity level";
    }
    return "";
  };

  const handleNewRecipientSeverityToggle = (severityValue) => {
    setNewRecipient((prev) => ({
      ...prev,
      severity: prev.severity.includes(severityValue)
        ? prev.severity.filter((s) => s !== severityValue)
        : [...prev.severity, severityValue]
    }));
  };

  const handleNewRecipientTypeToggle = (typeValue) => {
    setNewRecipient((prev) => ({
      ...prev,
      notificationType: prev.notificationType.includes(typeValue)
        ? prev.notificationType.filter((t) => t !== typeValue)
        : [...prev.notificationType, typeValue]
    }));
  };

  // Location-related handlers
  const handleLocationToggle = (locationId) => {
    setNewRecipient(prev => ({
      ...prev,
      locationTags: prev.locationTags.includes(locationId)
        ? prev.locationTags.filter(id => id !== locationId)
        : [...prev.locationTags, locationId]
    }));
  };

  const handleAddRecipient = async () => {
    if (!newRecipient.name || !newRecipient.email) {
      enqueueSnackbar('Please provide name and email (email is mandatory)', { variant: 'warning' });
      return;
    }
    const newErrors = {
      name: validateName(newRecipient.name),
      email: validateEmail(newRecipient.email),
      phoneNumber: validatePhone(newRecipient.phoneNumber),
    };

    setErrors(newErrors);

    const hasErrors = Object.values(newErrors).some(Boolean);
    if (hasErrors) return;

    const result = await onSave(newRecipient);
    if (result?.success) {
      enqueueSnackbar('Recipient added successfully', { variant: 'success' });
      setNewRecipient({
        name: '',
        email: '',
        phoneNumber: '',
        severity: [], // Reset to nothing selected
        notificationType: [], // Reset to nothing selected
        locationTags: []
      });
      setErrors({
        name: "",
        email: "",
        phoneNumber: "",
        notificationType: "",
        severity: "",
      });
    } else {
      enqueueSnackbar(result?.message || 'Failed to add recipient', { variant: 'error' });
    }
  };

  const isFormValid =
    !validateName(newRecipient.name) &&
    !validateEmail(newRecipient.email) &&
    !validatePhone(newRecipient.phoneNumber)
  // newRecipient.notificationType.length > 0 &&
  // newRecipient.severity.length > 0;

  const handleRemoveRecipientClick = (recipient) => {
    setRecipientToDelete(recipient);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!recipientToDelete) return;

    const recipientId = recipientToDelete._id;
    setDeleteConfirmOpen(false);

    try {
      setLoadingRecipients(prev => ({ ...prev, [recipientId]: 'remove' }));

      const apiRequest = (await import('utils/Apirequest')).default;
      const request = apiRequest();

      const { data } = await request('delete', `notification-config/${recipientId}`, {}, {}, {});

      if (data?.success) {
        enqueueSnackbar('Recipient removed successfully', { variant: 'success' });
        if (onRefresh) await onRefresh();
      } else {
        enqueueSnackbar('Failed to remove recipient', { variant: 'error' });
      }
    } catch (error) {
      console.error('Remove recipient error:', error);
      enqueueSnackbar('Error removing recipient', { variant: 'error' });
    } finally {
      setLoadingRecipients(prev => {
        const newState = { ...prev };
        delete newState[recipientId];
        return newState;
      });
      setRecipientToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setRecipientToDelete(null);
  };

  const handleEditRecipientClick = (recipient) => {
    setRecipientToEdit(recipient);
    // If no location tags were selected, pre-select all locations
    const locationTagsToSet = (recipient.locationTags && recipient.locationTags.length > 0)
      ? recipient.locationTags.map(tag => tag._id || tag) // Extract IDs from objects or use as-is if already IDs
      : locations.map(loc => loc._id);

    setEditRecipient({
      name: recipient.name,
      email: recipient.email,
      phoneNumber: recipient.phoneNumber || '',
      severity: recipient.severity || [],
      notificationType: recipient.notificationType || [],
      locationTags: locationTagsToSet,
    });
    setEditModalOpen(true);
  };

  const handleEditRecipientSeverityToggle = (severityValue) => {
    setEditRecipient((prev) => ({
      ...prev,
      severity: prev.severity.includes(severityValue)
        ? prev.severity.filter((s) => s !== severityValue)
        : [...prev.severity, severityValue]
    }));
  };

  const handleEditRecipientTypeToggle = (typeValue) => {
    setEditRecipient((prev) => ({
      ...prev,
      notificationType: prev.notificationType.includes(typeValue)
        ? prev.notificationType.filter((t) => t !== typeValue)
        : [...prev.notificationType, typeValue]
    }));
  };

  const handleUpdateRecipient = async () => {
    if (!editRecipient.name || !editRecipient.email) {
      enqueueSnackbar('Please provide name and email', { variant: 'warning' });
      return;
    }

    // if (editRecipient.notificationType.length === 0) {
    //   enqueueSnackbar('Select at least one notification type', { variant: 'warning' });
    //   return;
    // }

    // if (editRecipient.severity.length === 0) {
    //   enqueueSnackbar('Select at least one severity level', { variant: 'warning' });
    //   return;
    // }

    try {
      const apiRequest = (await import('utils/Apirequest')).default;
      const request = apiRequest();

      const { data } = await request(
        'put',
        `notification-config/${recipientToEdit._id}`,
        editRecipient,
        {},
        {}
      );

      if (data?.success) {
        enqueueSnackbar('Recipient updated successfully', { variant: 'success' });
        setEditModalOpen(false);
        setRecipientToEdit(null);
        if (onRefresh) await onRefresh();
      } else {
        enqueueSnackbar(data?.message || 'Failed to update recipient', { variant: 'error' });
      }
    } catch (error) {
      console.error('Update recipient error:', error);
      enqueueSnackbar('Error updating recipient', { variant: 'error' });
    }
  };

  const handleCancelEdit = () => {
    setEditModalOpen(false);
    setRecipientToEdit(null);
  };

  return (
    <MDBox>
      {/* Explanation Banner */}
      {/* <MDBox
        mb={3}
        p={2}
        borderRadius="8px"
        sx={{
          backgroundColor: darkMode ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)',
          border: `1px solid ${darkMode ? 'rgba(33, 150, 243, 0.3)' : 'rgba(33, 150, 243, 0.2)'}`,
        }}
      >
        <MDBox display="flex" alignItems="flex-start" gap={1.5}>
          <InfoOutlinedIcon sx={{ color: '#2196f3', fontSize: '20px', mt: 0.3 }} />
          <MDBox flex={1}>
            <MDTypography variant="caption" fontWeight="bold" color={darkMode ? 'white' : 'dark'} display="block" mb={0.5}>
              ℹ️ How It Works
            </MDTypography>
            <MDTypography variant="caption" color={darkMode ? 'white' : 'text'} display="block" sx={{ lineHeight: 1.6 }}>
              Select the notification types and severities you want. You'll receive notifications
              for <strong>EVERY combination</strong> of type + severity you select. See the live preview below.
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox> */}

      <Grid container spacing={3}>
        {/* Add Recipient Section */}
        <Grid item xs={12}>
          <Card
            sx={{
              backgroundColor: darkMode ? '#202940' : '#f8f9fa',
              border: `1px solid ${darkMode ? '#344767' : '#e9ecef'}`,
              boxShadow: 'none',
            }}
          >
            <CardContent>
              <MDTypography variant="h6" fontWeight="medium" color={darkMode ? 'white' : 'dark'} mb={2}>
                Add Recipient
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <MDInput
                    fullWidth
                    label="Name"
                    value={newRecipient.name}
                    error={Boolean(errors.name)}
                    helperText={errors.name}
                    FormHelperTextProps={{
                      sx: {
                        color: 'error.main',
                        marginLeft: '4px',
                        fontSize: '0.75rem',
                      },
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewRecipient({ ...newRecipient, name: value });
                      setErrors({ ...errors, name: validateName(value) });
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MDInput
                    fullWidth
                    label="Email *"
                    type="email"
                    value={newRecipient.email}
                    error={Boolean(errors.email)}
                    helperText={errors.email}
                    FormHelperTextProps={{
                      sx: { color: 'error.main', fontSize: '0.75rem' },
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewRecipient({ ...newRecipient, email: value });
                      setErrors({ ...errors, email: validateEmail(value) });
                    }}
                  />

                </Grid>
                <Grid item xs={12} md={4}>
                  <MDInput
                    fullWidth
                    type="tel"
                    label="Phone Number (Optional)"
                    value={newRecipient.phoneNumber}
                    error={Boolean(errors.phoneNumber)}
                    helperText={errors.phoneNumber}
                    placeholder="+971501234567"
                    FormHelperTextProps={{
                      sx: {
                        color: 'error.main',
                        marginLeft: '4px',
                        fontSize: '0.75rem',
                      },
                    }}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewRecipient({ ...newRecipient, phoneNumber: value });
                      setErrors({ ...errors, phoneNumber: validatePhone(value) });
                    }}
                  />
                </Grid>

                {/* Location Tags */}
                <Grid item xs={12}>
                  <MDTypography variant="button" fontWeight="medium" color={darkMode ? 'white' : 'dark'} mb={1}>
                    Tag Locations (Optional)
                  </MDTypography>
                  <MDTypography variant="caption" color="text" display="block" mb={1.5} fontSize="0.7rem">
                    Select specific locations for this recipient. If no location is selected, they will receive notifications for ALL locations.
                  </MDTypography>
                  {loadingLocations ? (
                    <MDBox display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </MDBox>
                  ) : (
                    <MDBox>
                      {locations.length > 5 && (
                        <MDInput
                          fullWidth
                          placeholder="Search locations..."
                          value={locationSearch}
                          onChange={(e) => setLocationSearch(e.target.value)}
                          sx={{ mb: 2 }}
                        />
                      )}
                      <MDBox sx={{ maxHeight: '200px', overflowY: 'auto', border: `1px solid ${darkMode ? '#344767' : '#e0e0e0'}`, borderRadius: 1, p: 1 }}>
                        <Grid container spacing={1}>
                          {locations
                            .filter(location =>
                              location.locationName.toLowerCase().includes(locationSearch.toLowerCase()) ||
                              location.address?.toLowerCase().includes(locationSearch.toLowerCase())
                            )
                            .map((location) => (
                              <Grid item xs={12} key={location._id}>
                                <FormControlLabel
                                  sx={{ alignItems: 'flex-start', m: 0 }}
                                  control={
                                    <Checkbox
                                      checked={newRecipient.locationTags.includes(location._id)}
                                      onChange={() => handleLocationToggle(location._id)}
                                      color="primary"
                                      size="small"
                                      sx={{ pt: 0.5 }}
                                    />
                                  }
                                  label={
                                    <MDBox>
                                      <MDTypography variant="caption" fontWeight="medium" display="block">
                                        {location.locationName}
                                      </MDTypography>
                                      <MDTypography variant="caption" color="text" fontSize="0.65rem">
                                        {location.address}
                                      </MDTypography>
                                      <MDTypography variant="caption" color="info" fontSize="0.6rem" display="block">
                                        ⚡ {location.chargerInfo?.filter?.(charger => charger.status === 'Active')?.length || 0} Active, {location.chargerInfo?.length || 0} Total Chargers
                                      </MDTypography>
                                    </MDBox>
                                  }
                                />
                              </Grid>
                            ))}
                        </Grid>
                      </MDBox>
                    </MDBox>
                  )}
                  {newRecipient.locationTags.length === 0 && (
                    <MDBox mt={1} p={1} sx={{ backgroundColor: darkMode ? '#0f1729' : '#e3f2fd', borderRadius: 1 }}>
                      <MDTypography variant="caption" color="info" fontSize="0.7rem">
                        ℹ️ No locations selected - recipient will receive notifications for ALL locations
                      </MDTypography>
                    </MDBox>
                  )}
                </Grid>

                {/* Live Preview */}
                <Grid item xs={12}>
                  <NotificationPreview
                    selectedTypes={newRecipient.notificationType}
                    selectedSeverities={newRecipient.severity}
                    darkMode={darkMode}
                    severityLevels={severityLevels}
                  />
                </Grid>

                <Grid item xs={12}>
                  <MDButton
                    variant="gradient"
                    color={sidenavColor}
                    startIcon={<AddIcon />}
                    onClick={handleAddRecipient}
                    disabled={!isFormValid}
                    fullWidth
                  >
                    Add Recipient
                  </MDButton>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Existing Recipients */}
        <Grid item xs={12}>
          <MDTypography variant="h6" fontWeight="medium" color={darkMode ? 'white' : 'dark'} mb={2}>
            Existing Recipients ({existingRecipients.length})
          </MDTypography>
          {existingRecipients.length === 0 ? (
            <Card sx={{ backgroundColor: darkMode ? '#202940' : '#f8f9fa', p: 3, textAlign: 'center' }}>
              <MDTypography variant="body2" color="text">
                No recipients configured yet. Add your first recipient above.
              </MDTypography>
            </Card>
          ) : (
            existingRecipients.map((recipient) => (
              <Accordion
                key={recipient._id}
                sx={{
                  mb: 2,
                  backgroundColor: darkMode ? '#202940' : '#fff',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <MDBox display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <MDBox display="flex" alignItems="center" gap={2}>
                      <PersonIcon sx={{ color: sidenavColor }} />
                      <MDBox>
                        <MDTypography variant="button" fontWeight="medium" color={darkMode ? 'white' : 'dark'}>
                          {recipient.name}
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                    <MDBox display="flex" gap={1}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditRecipientClick(recipient);
                        }}
                        size="small"
                      >
                        <EditIcon color="primary" />
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveRecipientClick(recipient);
                        }}
                        disabled={loadingRecipients[recipient._id] === 'remove'}
                        size="small"
                      >
                        {loadingRecipients[recipient._id] === 'remove' ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon color="error" />
                        )}
                      </IconButton>
                    </MDBox>
                  </MDBox>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    {/* <Grid item xs={12}>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? 'white' : 'dark'}>
                        Notification Types:
                      </MDTypography>
                      <MDBox display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {recipient.notificationType?.map((type) => {
                          const typeInfo = NOTIFICATION_TYPES.find(t => t.value === type);
                          return (
                            <Chip
                              key={type}
                              label={`${typeInfo?.icon} ${typeInfo?.label}`}
                              size="small"
                              variant="outlined"
                            />
                          );
                        })}
                      </MDBox>
                    </Grid>
                    <Grid item xs={12}>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? 'white' : 'dark'}>
                        Severity Levels:
                      </MDTypography>
                      <MDBox display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {recipient.severity?.map((sev) => {
                          const sevInfo = severityLevels.find(s => s.value === sev);
                          return (
                            <Chip
                              key={sev}
                              label={sevInfo?.label}
                              size="small"
                              sx={{ backgroundColor: sevInfo?.color, color: 'white' }}
                            />
                          );
                        })}
                      </MDBox>
                    </Grid> */}
                    <Grid item xs={12}>
                      <MDTypography variant="caption" fontWeight="bold" color={darkMode ? 'white' : 'dark'}>
                        Location Tags:
                      </MDTypography>
                      <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
                        {(recipient.locationTags && recipient.locationTags.length > 0) ? (
                          <MDBox display="flex" flexWrap="wrap" gap={1}>
                            {recipient.locationTags.map((tagLocation) => {
                              // Find the complete location data from the locations state
                              const completeLocation = locations.find(loc => loc._id === tagLocation._id || loc._id === tagLocation);
                              const displayLocation = completeLocation || tagLocation;
                              return (
                                <Chip
                                  key={displayLocation._id}
                                  label={`${displayLocation.locationName} (${displayLocation.chargerInfo?.filter?.(charger => charger.status === 'Active')?.length || 0} Active, ${displayLocation.chargerInfo?.length || 0} Total Chargers)`}
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                />
                              );
                            })}
                          </MDBox>
                        ) : (
                          <MDTypography variant="caption" color="text" sx={{ fontStyle: 'italic' }}>
                            All locations (no specific tags)
                          </MDTypography>
                        )}
                      </MDBox>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))
          )}
        </Grid>
      </Grid>

      {/* Edit Recipient Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? '#202940' : '#fff',
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={1}>
              <EditIcon color="primary" />
              <MDTypography variant="h5" fontWeight="medium" color={darkMode ? 'white' : 'dark'}>
                Edit Recipient
              </MDTypography>
            </MDBox>
            <IconButton onClick={handleCancelEdit}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                label="Name"
                value={editRecipient.name}
                onChange={(e) => setEditRecipient({ ...editRecipient, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                type="email"
                label="Email *"
                value={editRecipient.email}
                onChange={(e) => setEditRecipient({ ...editRecipient, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <MDInput
                fullWidth
                type="tel"
                label="Phone Number (Optional)"
                value={editRecipient.phoneNumber}
                placeholder="+971501234567"
                onChange={(e) => setEditRecipient({ ...editRecipient, phoneNumber: e.target.value })}
              />
            </Grid>
            {/* Location Tags */}
            <Grid item xs={12}>
              <MDTypography variant="button" fontWeight="medium" color={darkMode ? 'white' : 'dark'} mb={1}>
                Tag Locations (Optional)
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block" mb={1.5} fontSize="0.7rem">
                Select specific locations for this recipient. If no location is selected, they will receive notifications for ALL locations.
              </MDTypography>
              {loadingLocations ? (
                <MDBox display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={24} />
                </MDBox>
              ) : (
                <MDBox>
                  {locations.length > 5 && (
                    <MDInput
                      fullWidth
                      placeholder="Search locations..."
                      value={locationSearch}
                      onChange={(e) => setLocationSearch(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                  )}
                  <MDBox sx={{ maxHeight: '200px', overflowY: 'auto', border: `1px solid ${darkMode ? '#344767' : '#e0e0e0'}`, borderRadius: 1, p: 1 }}>
                    <Grid container spacing={1}>
                      {locations
                        .filter(location =>
                          location.locationName.toLowerCase().includes(locationSearch.toLowerCase()) ||
                          location.address?.toLowerCase().includes(locationSearch.toLowerCase())
                        )
                        .map((location) => (
                          <Grid item xs={12} key={location._id}>
                            <FormControlLabel
                              sx={{ alignItems: 'flex-start', m: 0 }}
                              control={
                                <Checkbox
                                  checked={editRecipient.locationTags.includes(location._id)}
                                  onChange={() => {
                                    setEditRecipient(prev => ({
                                      ...prev,
                                      locationTags: prev.locationTags.includes(location._id)
                                        ? prev.locationTags.filter(id => id !== location._id)
                                        : [...prev.locationTags, location._id]
                                    }));
                                  }}
                                  color="primary"
                                  size="small"
                                  sx={{ pt: 0.5 }}
                                />
                              }
                              label={
                                <MDBox>
                                  <MDTypography variant="caption" fontWeight="medium" display="block">
                                    {location.locationName}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="text" fontSize="0.65rem">
                                    {location.address}
                                  </MDTypography>
                                  <MDTypography variant="caption" color="info" fontSize="0.6rem" display="block">
                                    ⚡ {location.chargerInfo?.filter?.(charger => charger.status === 'Active')?.length || 0} Active, {location.chargerInfo?.length || 0} Total Chargers
                                  </MDTypography>
                                </MDBox>
                              }
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </MDBox>
                </MDBox>
              )}
              {editRecipient.locationTags.length === 0 && (
                <MDBox mt={1} p={1} sx={{ backgroundColor: darkMode ? '#0f1729' : '#e3f2fd', borderRadius: 1 }}>
                  <MDTypography variant="caption" color="info" fontSize="0.7rem">
                    ℹ️ No locations selected - recipient will receive notifications for ALL locations
                  </MDTypography>
                </MDBox>
              )}
            </Grid>

            {/* Live Preview */}
            <Grid item xs={12}>
              <NotificationPreview
                selectedTypes={editRecipient.notificationType}
                selectedSeverities={editRecipient.severity}
                darkMode={darkMode}
                severityLevels={severityLevels}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <MDButton variant="outlined" color="secondary" onClick={handleCancelEdit}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color={sidenavColor}
            onClick={handleUpdateRecipient}
            disabled={!editRecipient.name || !editRecipient.email}
          >
            Update Recipient
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        PaperProps={{
          sx: {
            backgroundColor: darkMode ? '#202940' : '#fff',
            borderRadius: '12px',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <DeleteIcon color="error" />
            <MDTypography variant="h5" fontWeight="medium" color={darkMode ? 'white' : 'dark'}>
              Confirm Delete
            </MDTypography>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" color="text" sx={{ mb: 2 }}>
            Are you sure you want to remove this recipient?
          </MDTypography>
          {recipientToDelete && (
            <Card sx={{ backgroundColor: darkMode ? '#1a2035' : '#f8f9fa', p: 2 }}>
              <MDTypography variant="button" fontWeight="medium" color={darkMode ? 'white' : 'dark'}>
                {recipientToDelete.name}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                {recipientToDelete.email}
              </MDTypography>
            </Card>
          )}
          <MDTypography variant="caption" color="error" sx={{ mt: 2, display: 'block', fontStyle: 'italic' }}>
            This action cannot be undone.
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <MDButton variant="outlined" color="secondary" onClick={handleCancelDelete}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={handleConfirmDelete}>
            Delete Recipient
          </MDButton>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}

export default NotificationRecipientSettings;
